import { useI18n } from '@ecom/i18n';
import { Button, LanguageSwitcher, type UseThemeResult } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ShopSwitcher } from './ShopSwitcher';

import { ViewShopButton } from '@/features/share/components/ViewShopButton';
import type { UseActiveShopResult } from '@/features/shops/use-active-shop';
import type { LiveStatus } from '@/lib/live-updates';
import { useLocale } from '@/lib/locale';
import { useSession } from '@/lib/session';

/**
 * Top bar, responsive. Every control of the desktop bar stays reachable at every width; what changes is where it sits:
 *   - below `lg` (1024 px): a menu button opens the navigation (`MobileNav`), the sidebar being hidden;
 *   - below `sm` (640 px): the search field becomes its icon (same command palette), the shop name moves into the
 *     « Plus d'options » menu;
 *   - below `xl` (1280 px): language, theme and sign-out are grouped in « Plus d'options » — at 1024–1279 px the bar
 *     sits beside the sidebar and could not hold them all;
 *   - the « En direct » badge keeps its coloured dot everywhere, its label from `xl`.
 * Touch targets are 40 px.
 */
export function Topbar({
  onOpenPalette,
  onOpenNav,
  navOpen,
  theme: themeState,
  shop,
  liveStatus,
}: {
  onOpenPalette: () => void;
  onOpenNav: () => void;
  navOpen: boolean;
  theme: UseThemeResult;
  shop: UseActiveShopResult;
  liveStatus: LiveStatus;
}): ReactElement {
  // `toggleTheme` is called via `themeState.toggleTheme` rather than
  // destructured — see the identical note in CommandPalette.tsx.
  const { theme } = themeState;
  const { clearSession } = useSession();
  const { locale, setLocale } = useLocale();
  const { t } = useI18n();
  const navigate = useNavigate();

  const liveLabel = t(
    liveStatus === 'live'
      ? 'merchant.live.on'
      : liveStatus === 'connecting'
        ? 'merchant.live.connecting'
        : 'merchant.live.off',
  );

  const signOut = (): void => {
    clearSession();
    void navigate('/login', { replace: true });
  };
  const toggleTheme = (): void => {
    themeState.toggleTheme();
  };
  const themeLabel =
    theme === 'dark' ? t('merchant.topbar.lightMode') : t('merchant.topbar.darkMode');

  return (
    <header className="flex items-center gap-2 border-b border-line bg-surface px-3 pb-2.5 pt-[max(0.625rem,env(safe-area-inset-top))] sm:gap-3 sm:px-5 sm:pb-3 sm:pt-[max(0.75rem,env(safe-area-inset-top))]">
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
        <button
          type="button"
          onClick={onOpenNav}
          aria-label={t('merchant.topbar.menu')}
          aria-expanded={navOpen}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-line text-ink transition-colors hover:bg-surfaceHover lg:hidden"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>

        <button
          type="button"
          onClick={onOpenPalette}
          aria-label={t('merchant.topbar.searchPlaceholder')}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-line bg-ground text-inkFaint transition-colors hover:border-accent/40 sm:hidden"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="6.5" />
            <path d="m16 16 4 4" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onOpenPalette}
          className="hidden min-w-0 max-w-72 flex-1 items-center justify-between gap-2 rounded-md border border-line bg-ground px-3 py-2 text-sm text-inkFaint transition-colors hover:border-accent/40 sm:flex"
        >
          <span className="truncate">{t('merchant.topbar.searchPlaceholder')}</span>
          <kbd className="hidden shrink-0 rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-[11px] text-inkFaint md:inline">
            ⌘K
          </kbd>
        </button>
        <div className="hidden min-w-0 max-w-[14rem] shrink sm:block">
          <ShopSwitcher shop={shop} />
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <span
          className="inline-flex h-10 items-center gap-1.5 rounded-full px-2 text-xs text-inkSoft xl:h-auto xl:border xl:border-line xl:px-2.5 xl:py-1"
          title={t('merchant.live.hint')}
          role="status"
        >
          <span
            aria-hidden="true"
            className={`h-2 w-2 rounded-full ${liveStatus === 'live' ? 'bg-good' : liveStatus === 'connecting' ? 'bg-warn' : 'bg-inkFaint'}`}
          />
          <span className="sr-only xl:not-sr-only">{liveLabel}</span>
        </span>
        <ViewShopButton />
        <div className="hidden items-center gap-2 xl:flex">
          <LanguageSwitcher
            locale={locale}
            onChange={(next) => {
              setLocale(next);
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={toggleTheme}
            aria-label={t('merchant.topbar.themeToggleAria')}
          >
            {themeLabel}
          </Button>
          <Button variant="secondary" size="sm" onClick={signOut}>
            {t('merchant.topbar.logout')}
          </Button>
        </div>
        <MoreMenu>
          {(close) => (
            <>
              <div className="sm:hidden">
                <ShopSwitcher shop={shop} />
              </div>
              <LanguageSwitcher
                locale={locale}
                onChange={(next) => {
                  setLocale(next);
                  close();
                }}
              />
              <Button
                variant="secondary"
                onClick={() => {
                  toggleTheme();
                  close();
                }}
                aria-label={t('merchant.topbar.themeToggleAria')}
              >
                {themeLabel}
              </Button>
              <Button variant="secondary" onClick={signOut}>
                {t('merchant.topbar.logout')}
              </Button>
            </>
          )}
        </MoreMenu>
      </div>
    </header>
  );
}

/** « Plus d'options » (below `xl`): a small panel under its button, closed by a choice, Escape or a tap outside. */
function MoreMenu({ children }: { children: (close: () => void) => ReactElement }): ReactElement {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: PointerEvent): void => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative xl:hidden">
      <button
        ref={buttonRef}
        type="button"
        aria-label={t('merchant.topbar.more')}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((value) => !value);
        }}
        className="flex h-10 w-10 items-center justify-center rounded-md border border-line text-ink transition-colors hover:bg-surfaceHover"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
          <circle cx="5" cy="12" r="1.8" />
          <circle cx="12" cy="12" r="1.8" />
          <circle cx="19" cy="12" r="1.8" />
        </svg>
      </button>
      {open ? (
        <div
          id={panelId}
          className="absolute end-0 top-full z-50 mt-2 flex w-64 max-w-[calc(100vw-1.5rem)] flex-col gap-3 rounded-md border border-line bg-surface p-3 shadow-lg"
        >
          {children(() => {
            setOpen(false);
          })}
        </div>
      ) : null}
    </div>
  );
}
